import { PropsWithChildren, createContext, useContext, useMemo, useState } from "react";
import {
  CandidatesAddedByCampaignReportState,
  useCandidatesAddedByCampaignReportState,
} from "../../Reports/CandidatesAddedByCampaign/State/CandidatesAddedByCampaignReportState";
import { CampaignContact, CampaignReportTimeframe } from "../../App/Enums/CampaignTypes";

interface ICampaignProfileReportContext {
  startDate: Date;
  endDate: Date;
  numDaysInReport: number;
  timeframeOption: CampaignReportTimeframe;
  campaignId: string;
  campaignContact?: CampaignContact;
  showEmailReports?: boolean;
  showTextReports?: boolean;
  handleSetReportStartDate: (date: Date | null) => void;
  handleSetReportEndDate: (date: Date | null) => void;
  handleSetTimeframeOption: (option: CampaignReportTimeframe) => void;
  candidatesAddedState: CandidatesAddedByCampaignReportState;
}

const defaultEndDate = new Date(Date.now());
const defaultStartDate = new Date(Date.now() - 30 * 1000 * 60 * 60 * 24); // 30 days ago

const defaultReportContext = {
  startDate: defaultStartDate,
  endDate: defaultEndDate,
  numDaysInReport: 30,
  campaignId: "",
  campaignContact: CampaignContact.Email,
  handleSetReportEndDate: (date: Date | null) => {
    return;
  },
  handleSetReportStartDate: (date: Date | null) => {
    return;
  },
  candidatesAddedState: {} as CandidatesAddedByCampaignReportState,
  timeframeOption: CampaignReportTimeframe.Last30Days,
} as ICampaignProfileReportContext;

const CampaignProfileReportContext = createContext(defaultReportContext);

const useCampaignProfileReportContext = () => {
  return useContext(CampaignProfileReportContext);
};

const CampaignProfileReportContextProvider = ({
  children,
  campaignId,
  campaignContact,
}: PropsWithChildren<{ campaignId: string; campaignContact?: CampaignContact }>) => {
  const [reportEndDate, setReportEndDate] = useState(defaultReportContext.endDate);
  const [reportStartDate, setReportStartDate] = useState(defaultReportContext.startDate);
  const [timeframeOption, setTimeframeOption] = useState(CampaignReportTimeframe.Last30Days);

  const numDaysInReport = useMemo(() => {
    return Math.round((reportEndDate.valueOf() - reportStartDate.valueOf()) / (1000 * 60 * 60 * 24));
  }, [reportEndDate, reportStartDate]);

  // Only show email performance if contact is Email or Email/Phone
  const showEmailReports = campaignContact?.toLowerCase()?.includes("email") === true;
  // Only show text performance if contact is Phone or Email/Phone
  const showTextReports = campaignContact?.toLowerCase()?.includes("phone") === true;

  const candidatesAddedState = useCandidatesAddedByCampaignReportState(
    reportStartDate,
    reportEndDate,
    campaignId,
  );

  const handleSetReportEndDate = (date: Date | null) => {
    if (date) {
      setReportEndDate(date);
    }
  };

  const handleSetReportStartDate = (date: Date | null) => {
    if (date) {
      setReportStartDate(date);
    }
  };

  const handleSetTimeframeOption = (option: CampaignReportTimeframe) => {
    setTimeframeOption(option);
  };

  return (
    <CampaignProfileReportContext.Provider
      value={{
        campaignId,
        campaignContact,
        numDaysInReport,
        startDate: reportStartDate,
        endDate: reportEndDate,
        timeframeOption,
        showEmailReports,
        showTextReports,
        handleSetReportEndDate,
        handleSetReportStartDate,
        handleSetTimeframeOption,
        candidatesAddedState,
      }}
    >
      {children}
    </CampaignProfileReportContext.Provider>
  );
};

export { CampaignProfileReportContextProvider, useCampaignProfileReportContext };
